import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { JjamesButtonComponent } from 'shared-lib';
import { PaymentObligationDto } from '../../../api/gen';

export type ObligationAction = 'waive' | 'cancel' | 'reissue';

export interface ObligationActionDialogData {
    obligation: PaymentObligationDto;
    action: ObligationAction;
}

/**
 * Confirms one terminal action on an obligation and collects the mandatory reason.
 *
 * The reason is not paperwork: it is stored on the obligation and its audit row, and it is the only
 * record of why a charge was dropped. The save button stays disabled until something is typed for
 * that reason — the server rejects an empty one anyway, and finding that out after clicking is
 * worse than being told up front.
 */
@Component({
    selector: 'app-obligation-action-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, MatDialogModule, MatIconModule, JjamesButtonComponent, TranslatePipe],
    templateUrl: './obligation-action-dialog.component.html'
})
export class ObligationActionDialogComponent {
    private dialogRef = inject(MatDialogRef<ObligationActionDialogComponent>);
    readonly data = inject<ObligationActionDialogData>(MAT_DIALOG_DATA);

    reason = signal('');

    readonly canConfirm = computed(() => this.reason().trim().length > 0);

    readonly titleKey = computed(() => `obligations.${this.data.action}Title`);
    readonly bodyKey = computed(() => `obligations.${this.data.action}Body`);

    /**
     * The dismiss button names what happens if you back out, rather than always saying "Cancel".
     *
     * On the cancel-a-bill dialog the generic label produced two buttons reading "Cancel" in English —
     * one meaning "cancel the bill" and one meaning "cancel this dialog" — which is a coin flip on an
     * action that writes off revenue. (German never had the problem: "Stornieren" against
     * "Abbrechen".) Naming the outcome instead is clearer than any amount of colouring.
     */
    readonly dismissKey = computed(() =>
        this.data.action === 'cancel' ? 'obligations.cancelDismiss' : 'common.cancel');

    /** Cancel and reissue are corrections; waiving is a decision to forgo real revenue. */
    readonly isDestructive = computed(() => this.data.action === 'waive');

    onCancel(): void {
        this.dialogRef.close();
    }

    onConfirm(): void {
        if (!this.canConfirm()) return;
        this.dialogRef.close(this.reason().trim());
    }
}
